import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Spinner,
} from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import { adminListCategories } from "../../actions/categoryActions";
import {
  createJewelryHireItemAdmin,
  getJewelryHireItemAdmin,
  resetAdminJewelryHireCreate,
  resetAdminJewelryHireDetails,
  resetAdminJewelryHireUpdate,
  updateJewelryHireItemAdmin,
} from "../../actions/jewelryHireActions";
import { adminListSubcategories } from "../../actions/subcategoryActions";
import Uploader from "../../components/images/Uploader";
import { JEWELRY_HIRE_CATALOG_KEY } from "../../constants/shopMeta";
import { getApiErrorMessage } from "../../utils/errorMessages";

const getId = (value) => String(value?._id || value?.id || value || "");

export default function AdminJewelryHireItemFormScreen() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [images, setImages] = useState([]);
  const [specificationsText, setSpecificationsText] = useState("");
  const [displayOrder, setDisplayOrder] = useState(0);
  const [isPublished, setIsPublished] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const visibleSubcategories = useMemo(
    () =>
      subcategories.filter(
        (candidate) => !category || getId(candidate.parentId) === category,
      ),
    [category, subcategories],
  );

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError("");

      try {
        const [categoryData, subcategoryData, item] = await Promise.all([
          dispatch(
            adminListCategories({
              shopKey: JEWELRY_HIRE_CATALOG_KEY,
              pageSize: 200,
            }),
          ),
          dispatch(
            adminListSubcategories({
              shopKey: JEWELRY_HIRE_CATALOG_KEY,
              pageSize: 200,
            }),
          ),
          isEdit ? dispatch(getJewelryHireItemAdmin(id)) : Promise.resolve(null),
        ]);

        if (!active) return;

        setCategories(categoryData?.categories || []);
        setSubcategories(subcategoryData?.subcategories || []);

        if (item) {
          setName(item.name || "");
          setSlug(item.slug || "");
          setDescription(item.description || "");
          setCategory(getId(item.category));
          setSubcategory(getId(item.subcategory));
          setImages(Array.isArray(item.images) ? item.images : []);
          setSpecificationsText(
            Array.isArray(item.specifications)
              ? item.specifications.join("\n")
              : "",
          );
          setDisplayOrder(item.displayOrder ?? 0);
          setIsPublished(item.isPublished !== false);
        }
      } catch (loadError) {
        if (!active) return;
        setError(
          getApiErrorMessage(loadError, {
            fallback: "We could not load the jewellery hire form.",
          }),
        );
      } finally {
        if (active) setLoading(false);
      }
    };

    dispatch(resetAdminJewelryHireCreate());
    dispatch(resetAdminJewelryHireUpdate());
    load();

    return () => {
      active = false;
      dispatch(resetAdminJewelryHireDetails());
    };
  }, [dispatch, id, isEdit]);

  const submitHandler = async (event) => {
    event.preventDefault();

    const nextDisplayOrder = Number(displayOrder);
    const specifications = specificationsText
      .split("\n")
      .map((value) => value.trim())
      .filter(Boolean);

    if (!name.trim()) return toast.error("Item name is required.");
    if (!category) return toast.error("Category is required.");
    if (!subcategory) return toast.error("Subcategory is required.");
    if (
      !Number.isInteger(nextDisplayOrder) ||
      nextDisplayOrder < 0 ||
      nextDisplayOrder > 9999
    ) {
      return toast.error("Display order must be a whole number from 0 to 9999.");
    }
    if (specifications.length > 30) {
      return toast.error("Add no more than 30 specifications.");
    }
    if (specifications.some((value) => value.length > 240)) {
      return toast.error("Each specification must be 240 characters or fewer.");
    }

    const payload = {
      name: name.trim(),
      slug: slug.trim(),
      description: description.trim(),
      category,
      subcategory,
      images,
      specifications,
      displayOrder: nextDisplayOrder,
      isPublished,
    };

    setSaving(true);
    setError("");

    try {
      if (isEdit) {
        await dispatch(updateJewelryHireItemAdmin(id, payload));
        toast.success("Jewellery hire item updated.");
      } else {
        await dispatch(createJewelryHireItemAdmin(payload));
        toast.success("Jewellery hire item created.");
      }
      navigate("/admin/jewelry-hire");
    } catch (saveError) {
      setError(
        getApiErrorMessage(saveError, {
          fallback: "We could not save this jewellery hire item.",
        }),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container className="py-4">
      <Row className="justify-content-center">
        <Col xl={9}>
          <div className="d-flex justify-content-between align-items-center gap-3 mb-3">
            <div>
              <h1 className="mb-1">
                {isEdit
                  ? "Edit Jewellery Hire Item"
                  : "Create Jewellery Hire Item"}
              </h1>
              <div className="text-muted">
                Manage catalogue details, imagery and display order.
              </div>
            </div>
            <Button as={Link} to="/admin/jewelry-hire" variant="light">
              Back
            </Button>
          </div>

          <Card className="shadow-sm">
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : (
                <Form onSubmit={submitHandler}>
                  {error ? <Alert variant="danger">{error}</Alert> : null}

                  <Row className="g-3">
                    <Col md={8}>
                      <Form.Group controlId="jewelryItemName">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          value={name}
                          onChange={(event) => setName(event.target.value)}
                          maxLength={140}
                          required
                          disabled={saving}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group controlId="jewelryItemSlug">
                        <Form.Label>Slug</Form.Label>
                        <Form.Control
                          value={slug}
                          onChange={(event) => setSlug(event.target.value)}
                          placeholder="Generated from name"
                          maxLength={180}
                          disabled={saving}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mt-3" controlId="jewelryDescription">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={6}
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                      maxLength={5000}
                      disabled={saving}
                    />
                  </Form.Group>

                  <Form.Group
                    className="mt-3"
                    controlId="jewelrySpecifications"
                  >
                    <Form.Label>Details / specifications</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={5}
                      value={specificationsText}
                      onChange={(event) =>
                        setSpecificationsText(event.target.value)
                      }
                      placeholder="Add one detail per line"
                      disabled={saving}
                    />
                    <Form.Text className="text-muted">
                      Add one customer-facing detail per line (up to 30).
                    </Form.Text>
                  </Form.Group>

                  <Row className="g-3 mt-0">
                    <Col md={6}>
                      <Form.Group controlId="jewelryCategory">
                        <div className="d-flex justify-content-between align-items-center">
                          <Form.Label>Category</Form.Label>
                          <Link
                            to={`/admin/shop/${JEWELRY_HIRE_CATALOG_KEY}/categories/create`}
                            className="small"
                          >
                            Create category
                          </Link>
                        </div>
                        <Form.Select
                          value={category}
                          onChange={(event) => {
                            setCategory(event.target.value);
                            setSubcategory("");
                          }}
                          required
                          disabled={saving}
                        >
                          <option value="">Select category</option>
                          {categories.map((candidate) => (
                            <option key={getId(candidate)} value={getId(candidate)}>
                              {candidate.name}
                              {candidate.isPublished === false ? " (Draft)" : ""}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group controlId="jewelrySubcategory">
                        <div className="d-flex justify-content-between align-items-center">
                          <Form.Label>Subcategory</Form.Label>
                          <Link
                            to={`/admin/shop/${JEWELRY_HIRE_CATALOG_KEY}/subcategories/create`}
                            className="small"
                          >
                            Create subcategory
                          </Link>
                        </div>
                        <Form.Select
                          value={subcategory}
                          onChange={(event) => setSubcategory(event.target.value)}
                          required
                          disabled={saving || !category}
                        >
                          <option value="">Select subcategory</option>
                          {visibleSubcategories.map((candidate) => (
                            <option key={getId(candidate)} value={getId(candidate)}>
                              {candidate.name}
                              {candidate.isPublished === false ? " (Draft)" : ""}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3 mt-0 align-items-end">
                    <Col md={4}>
                      <Form.Group controlId="jewelryDisplayOrder">
                        <Form.Label>Display order</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          max="9999"
                          step="1"
                          value={displayOrder}
                          onChange={(event) =>
                            setDisplayOrder(event.target.value)
                          }
                          required
                          disabled={saving}
                        />
                        <Form.Text className="text-muted">
                          Lower numbers appear first.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col md={8}>
                      <Form.Group controlId="jewelryPublished">
                        <Form.Check
                          type="switch"
                          label="Published on the customer Jewellery Hire page"
                          checked={isPublished}
                          onChange={(event) =>
                            setIsPublished(event.target.checked)
                          }
                          disabled={saving}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Card className="mt-4">
                    <Card.Header>
                      <strong>Item Images</strong>
                    </Card.Header>
                    <Card.Body>
                      <Uploader
                        existingImages={images}
                        setImageUrls={setImages}
                        folder="jewelry-hire"
                      />
                      <Form.Text className="text-muted">
                        Upload images before saving. The first image is the cover.
                      </Form.Text>
                    </Card.Body>
                  </Card>

                  <div className="d-flex gap-2 mt-4">
                    <Button type="submit" variant="dark" disabled={saving}>
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Saving...
                        </>
                      ) : isEdit ? (
                        "Save changes"
                      ) : (
                        "Create item"
                      )}
                    </Button>
                    <Button
                      as={Link}
                      to="/admin/jewelry-hire"
                      variant="outline-secondary"
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
